/* eslint-disable @typescript-eslint/no-explicit-any -- existing annotations; retyping would change compile behavior */
import { useState, useEffect } from 'react';
import api from 'api';

/**
 * Generic fetcher: calls an API function, stores data + isLoading.
 * Re-fetches whenever `deps` change.
 */
function useReferenceDataFetcher<T = any>(
    fetcher: () => Promise<{ data: T }>,
    deps: unknown[] = []
) {
    const [data, setData] = useState<T | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setIsLoading(true);

        fetcher()
            .then((res) => {
                if (!cancelled) setData(res.data);
            })
            .catch((err) => {
                if (!cancelled) console.error(err);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return { data, isLoading, isFetching: isLoading };
}

/* ─── Static reference data hooks (no params, fetch once on mount) ─── */

export function useCountries() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getCountries(), []);
}

export function useNationalities() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getNationalities(), []);
}

export function useRegions() {
    // API returns a map of country name → region list
    return useReferenceDataFetcher<Record<string, string[]>>(
        () => api.referenceData.getRegions(),
        []
    );
}

export function useRelations() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getRelations(), []);
}

export function useRelationsChild() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getRelationsChild(), []);
}

export function useRelationsNonChild() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getRelationsNonChild(), []);
}

export function useEducationLevels() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getEducationLevels(), []);
}

export function useExpertiseList() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getExpertiseList(), []);
}

export function useWorkPermits() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getWorkPermits(), []);
}

export function useFieldOfStudyOptions() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getFieldOfStudyOptions(), []);
}

/** Field-of-study options for a destination market (AU / CA / UK) — returns { id, name }[]. */
export function useFieldOfStudyByCountryCode(countryCode: string) {
    return useReferenceDataFetcher<{ id: number; name: string; country_code?: string }[]>(
        () =>
            countryCode
                ? api.assessment_options.getFieldOfStudyOptions(countryCode)
                : Promise.resolve({ data: [] }),
        [countryCode]
    );
}

/** Occupation / profession options keyed by country code (AU / CA / UK). */
export function useOccupationsByCountryCode(countryCode: string) {
    return useReferenceDataFetcher<{ id: number; name: string; country_code?: string }[]>(
        () =>
            countryCode
                ? api.assessment_options.getIndexProfessions(countryCode)
                : Promise.resolve({ data: [] }),
        [countryCode]
    );
}

export function useNominationOptions() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getNominationOptions(), []);
}

/**
 * Nomination options for job offers — UK uses a different endpoint (AssessmentV2 JobOfferForm).
 * Pass the selected job-offer country (e.g. "United Kingdom").
 */
export function useNominationOptionsByCountry(country: string) {
    return useReferenceDataFetcher<string[]>(
        () =>
            country
                ? api.assessment_options.getNominationOptions(country)
                : Promise.resolve({ data: [] }),
        [country]
    );
}

/** UK / Canada institution lists (AssessmentV2 Education). */
export function useUkInstitutions(enabled = true) {
    return useReferenceDataFetcher<string[]>(
        () =>
            enabled
                ? api.assessment_options.getUkInstitutionOptions()
                : Promise.resolve({ data: [] }),
        [enabled]
    );
}

export function useCaInstitutions(enabled = true) {
    return useReferenceDataFetcher<string[]>(
        () =>
            enabled
                ? api.assessment_options.getCaInstitutionOptions()
                : Promise.resolve({ data: [] }),
        [enabled]
    );
}

export function useAuInstitutions(enabled = true) {
    return useReferenceDataFetcher<string[]>(
        () =>
            enabled
                ? api.assessment_options.getAuInstitutionOptions()
                : Promise.resolve({ data: [] }),
        [enabled]
    );
}

export function useBusinessSectors() {
    return useReferenceDataFetcher<string[]>(() => api.referenceData.getBusinessSectors(), []);
}

export function useRegionOccupations() {
    return useReferenceDataFetcher(() => api.referenceData.getRegionOccupations(), []);
}

/* ─── Parameterised hooks (re-fetch when the param changes) ─── */

export function useCountryResidentialStatus(country: string) {
    return useReferenceDataFetcher<string[]>(
        () =>
            country
                ? api.referenceData.getResidentialStatus(country)
                : Promise.resolve({ data: [] }),
        [country]
    );
}

export function useProfessions(country: string) {
    return useReferenceDataFetcher<string[]>(
        () => (country ? api.referenceData.getProfessions(country) : Promise.resolve({ data: [] })),
        [country]
    );
}
